import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig, apiConfig } from "../authConfig";

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
export const initializeMsal = async () => {
  await msalInstance.initialize();
  
  // Handle redirect response
  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    msalInstance.setActiveAccount(response.account);
  }
  
  // Set active account if available
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
  
  return msalInstance;
};

// Login with popup
export const login = async () => {
  try {
    const response = await msalInstance.loginPopup({
      scopes: apiConfig.scopes,
    });
    msalInstance.setActiveAccount(response.account);
    return response;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

// Login with redirect (alternative)
export const loginRedirect = async () => {
  try {
    await msalInstance.loginRedirect({
      scopes: apiConfig.scopes,
    });
  } catch (error) {
    console.error("Login redirect failed:", error);
    throw error;
  }
};

// Logout
export const logout = async () => {
  const account = msalInstance.getActiveAccount();
  if (account) {
    // Clear any cached account data (will be imported dynamically to avoid circular deps)
    try {
      const { AccountService } = await import('./PaiPlatformService');
      AccountService.clearCache();
    } catch (e) {
      console.warn('Failed to clear account cache:', e);
    }
    
    await msalInstance.logoutPopup({
      account: account,
    });
  }
};

// Get current user
export const getCurrentUser = () => {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  
  // Use 'oid' claim from idTokenClaims as the primary user ID
  // This is the Azure AD Object ID which should match backend expectations
  const oid = account.idTokenClaims?.oid;
  
  return {
    id: oid || account.localAccountId, // Prefer oid, fallback to localAccountId
    localAccountId: account.localAccountId, // Keep for MSAL cache operations
    email: account.username,
    name: account.name || account.username,
    tenantId: account.tenantId,
    // Expose all claims for debugging
    claims: account.idTokenClaims,
  };
};

// Get access token for API calls
export const getAccessToken = async () => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error("No active account. Please login first.");
  }
  
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: apiConfig.scopes,
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expired or needs refresh, prompt for login
      const response = await msalInstance.acquireTokenPopup({
        scopes: apiConfig.scopes,
      });
      return response.accessToken;
    }
    throw error;
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return msalInstance.getActiveAccount() !== null;
};
